import { RestApiSenderAuthType } from '../../enums/auth/rest-api-sender-auth-type';
import { RequestMethod } from '../../enums/request-method';
import { RestApiPayloadType } from '../../enums/rest-api-payload-type';
import { UrlProtocol } from '../../enums/url-protocol';
import { RestApiSenderAuthConfig } from '../../types/rest-api-sender-auth-config';
import { DestinationTaskConfigBase } from '../config-objects/destination-service-config-base';

/**
 * Configuration for a RestApiSender task — sends the current flow data (or a static payload)
 * to an external HTTP endpoint and replaces processingData with the response body.
 */
export class RestApiSenderConfig extends DestinationTaskConfigBase {
	/**
	 * HTTP method to use when calling the endpoint (GET=0, POST=1, PUT=2, DELETE=3, PATCH=4).
	 * @default RequestMethod.GET
	 */
	httpMethodType = RequestMethod.GET;

	/**
	 * URL protocol: Https=0 (default), Http=1.
	 * @default UrlProtocol.Https
	 */
	urlProtocol: UrlProtocol = UrlProtocol.Https;

	/**
	 * The full URL of the external endpoint to call.
	 * @default ''
	 */
	url = '';

	/**
	 * Extra HTTP headers to include in every request, as a key-value object.
	 * Merged with auth headers. Content-Type is set automatically from payloadType if omitted.
	 * @default {}
	 */
	additionalHeaders?: { [key: string]: string } = {};

	/**
	 * Extra URL query parameters appended to the request URL, as a key-value object.
	 * When authType is APIKey and addTo is 'queryParams', the API key is also appended here.
	 * @default {}
	 */
	additionalQueryParams?: { [key: string]: string } = {};

	/**
	 * How the request body is encoded: JSON=0, FormData=1, EncodedFormData=2.
	 * @default RestApiPayloadType.JSON
	 */
	payloadType: RestApiPayloadType = RestApiPayloadType.JSON;

	/**
	 * Static key-value pairs sent as form fields. Only used when payloadType is FormData or EncodedFormData.
	 * @default {}
	 */
	formData: { [key: string]: string } = {};

	/**
	 * When true, sends staticPayload instead of the live flow processingData.
	 * @default false
	 */
	sendStaticPayload = false;

	/**
	 * Fixed JSON object to send when sendStaticPayload is true. Ignored otherwise.
	 * @default {}
	 */
	staticPayload = {};

	/**
	 * When true, the original input processingData is merged with the API response in the output.
	 * Useful when downstream tasks need both the source data and the response.
	 * @default false
	 */
	preserveInputData = false;

	/**
	 * Enables automatic retry on non-2xx response or network error.
	 * @default false
	 */
	enableRetries = false;

	/**
	 * Number of retry attempts when enableRetries is true. Ignored otherwise.
	 * @default 0
	 */
	retryAmount = 0;

	/**
	 * When true, HTTP response headers are included in the output processingData alongside the body.
	 * @default false
	 */
	includeResponseHeaders = false;

	/**
	 * Authentication strategy: NoAuth=1, BasicAuth=2, APIKey=3, BearerToken=4, JWTBearer=5, AWSSignature=6.
	 * @default RestApiSenderAuthType.NoAuth
	 */
	authType: RestApiSenderAuthType = RestApiSenderAuthType.NoAuth;

	/**
	 * Authentication credentials. Shape depends on authType — see RestApiSenderAuthConfig union type.
	 * Credential string values use an AccessToken wrapper: { value: string, useVariableToken: boolean }.
	 */
	authConfig?: RestApiSenderAuthConfig;

	/**
	 * Delay in milliseconds between retry attempts when enableRetries is true.
	 * @default 0
	 */
	retryDelay = 0;

	constructor(init?: Partial<RestApiSenderConfig>) {
		super(init);
		if (init) {
			Object.assign(this, init);
		}
	}
}
